import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  ClipboardList,
  Loader2,
  Search,
  Plus,
  Eye,
  Trash2,
  Filter,
  Calendar,
  User,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Lock,
  ShieldAlert
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const ESTADO_CONFIG = {
  pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  aprobada: { label: "Aprobada", color: "bg-blue-100 text-blue-700 border-blue-200", icon: CheckCircle },
  rechazada: { label: "Rechazada", color: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
  parcial: { label: "Entrega Parcial", color: "bg-purple-100 text-purple-700 border-purple-200", icon: Package },
  entregada: { label: "Entregada", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle },
  cancelada: { label: "Cancelada", color: "bg-slate-100 text-slate-700 border-slate-200", icon: XCircle }
};

const PRIORIDAD_CONFIG = {
  baja: { label: "Baja", color: "bg-slate-100 text-slate-600" },
  normal: { label: "Normal", color: "bg-blue-100 text-blue-600" },
  alta: { label: "Alta", color: "bg-orange-100 text-orange-600" },
  urgente: { label: "Urgente", color: "bg-red-100 text-red-600" }
};

export default function RequisicionesPage() {
  const [requisiciones, setRequisiciones] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedReq, setSelectedReq] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Delete authorization states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [reqToDelete, setReqToDelete] = useState(null);
  const [authPassword, setAuthPassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  
  const pageSize = 20;
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("limit", pageSize);
      params.append("skip", page * pageSize);
      if (estadoFilter !== "all") params.append("estado", estadoFilter);

      const [reqRes, statsRes] = await Promise.all([
        axios.get(`${API}/requisiciones?${params.toString()}`, getAuthHeaders()),
        axios.get(`${API}/requisiciones/stats/dashboard`, getAuthHeaders())
      ]);

      setRequisiciones(reqRes.data.requisiciones || []);
      setTotal(reqRes.data.total || 0);
      setStats(statsRes.data || {});
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Error al cargar requisiciones");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, page, estadoFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleDateString("es", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const handleDelete = (req) => {
    setReqToDelete(req);
    setAuthPassword("");
    setShowDeleteDialog(true);
  };

  const executeDelete = async () => {
    if (!reqToDelete) return;
    
    if (!authPassword.trim()) {
      toast.error("Ingrese su contraseña para autorizar");
      return;
    }
    
    setDeleting(true);
    try {
      const response = await axios.post(
        `${API}/requisiciones/${reqToDelete.id}/delete-with-auth`,
        { password: authPassword },
        getAuthHeaders()
      );
      
      toast.success(`Requisición ${reqToDelete.numero} eliminada. Autorizado por: ${response.data.authorized_by}`);
      setShowDeleteDialog(false);
      setReqToDelete(null);
      fetchData();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Error al eliminar";
      toast.error(errorMsg);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = async (req) => {
    if (!window.confirm(`¿Cancelar la requisición ${req.numero}?`)) return;
    
    try {
      await axios.post(`${API}/requisiciones/${req.id}/cancelar`, {}, getAuthHeaders());
      toast.success("Requisición cancelada");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cancelar");
    }
  };

  const viewDetail = (req) => {
    setSelectedReq(req);
    setShowDetailDialog(true);
  };

  const filteredRequisiciones = requisiciones.filter(req => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      req.numero?.toLowerCase().includes(search) ||
      req.solicitante_nombre?.toLowerCase().includes(search) ||
      req.motivo?.toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="requisiciones-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Requisiciones de Materiales
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Gestión de solicitudes de materiales
              </p>
            </div>
          </div>
          <Button onClick={() => navigate("/requisiciones/nueva")} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Requisición
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card className="border-amber-200 bg-amber-50/50 cursor-pointer hover:shadow-md" onClick={() => setEstadoFilter("pendiente")}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-amber-600">Pendientes</p>
                  <p className="text-2xl font-bold text-amber-700">{stats.pendientes || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50/50 cursor-pointer hover:shadow-md" onClick={() => setEstadoFilter("aprobada")}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-600">Aprobadas</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.aprobadas || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50/50 cursor-pointer hover:shadow-md" onClick={() => setEstadoFilter("parcial")}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-purple-600">Parciales</p>
                  <p className="text-2xl font-bold text-purple-700">{stats.parciales || 0}</p>
                </div>
                <Package className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50/50 cursor-pointer hover:shadow-md" onClick={() => setEstadoFilter("entregada")}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-600">Entregadas</p>
                  <p className="text-2xl font-bold text-green-700">{stats.entregadas || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50/50 cursor-pointer hover:shadow-md" onClick={() => setEstadoFilter("rechazada")}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-red-600">Rechazadas</p>
                  <p className="text-2xl font-bold text-red-700">{stats.rechazadas || 0}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 cursor-pointer hover:shadow-md" onClick={() => setEstadoFilter("all")}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Total</p>
                  <p className="text-2xl font-bold text-slate-700">{stats.total || 0}</p>
                </div>
                <ClipboardList className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar por número, solicitante..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="w-48">
                <Select value={estadoFilter} onValueChange={(v) => { setEstadoFilter(v); setPage(0); }}>
                  <SelectTrigger>
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="pendiente">Pendientes</SelectItem>
                    <SelectItem value="aprobada">Aprobadas</SelectItem>
                    <SelectItem value="parcial">Entrega Parcial</SelectItem>
                    <SelectItem value="entregada">Entregadas</SelectItem>
                    <SelectItem value="rechazada">Rechazadas</SelectItem>
                    <SelectItem value="cancelada">Canceladas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <Card>
            <ScrollArea className="h-[calc(100vh-450px)]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Solicitante</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Prioridad</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequisiciones.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                        <p className="text-slate-500">No se encontraron requisiciones</p>
                        <Button variant="link" onClick={() => navigate("/requisiciones/nueva")}>
                          Crear nueva requisición
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRequisiciones.map((req) => {
                      const estadoConfig = ESTADO_CONFIG[req.estado] || ESTADO_CONFIG.pendiente;
                      const prioridadConfig = PRIORIDAD_CONFIG[req.prioridad] || PRIORIDAD_CONFIG.normal;
                      const EstadoIcon = estadoConfig.icon;
                      
                      return (
                        <TableRow key={req.id}>
                          <TableCell className="font-mono font-medium">{req.numero}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              {req.solicitante_nombre}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              {formatDate(req.created_at)}
                            </div>
                            {req.fecha_requerida && (
                              <p className="text-xs text-slate-500">Req: {formatDate(req.fecha_requerida)}</p>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={prioridadConfig.color}>{prioridadConfig.label}</Badge>
                          </TableCell>
                          <TableCell>{req.items?.length || 0} items</TableCell>
                          <TableCell>
                            <Badge className={`${estadoConfig.color} flex items-center gap-1 w-fit`}>
                              <EstadoIcon className="w-3 h-3" />
                              {estadoConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => viewDetail(req)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              {req.estado === "pendiente" && (
                                <>
                                  <Button variant="ghost" size="icon" onClick={() => handleCancel(req)}>
                                    <XCircle className="w-4 h-4 text-orange-500" />
                                  </Button>
                                  <Button variant="ghost" size="icon" onClick={() => handleDelete(req)}>
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </ScrollArea>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-slate-500">
                  Mostrando {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} de {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">Página {page + 1} de {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Requisición {selectedReq?.numero}
              </DialogTitle>
            </DialogHeader>
            
            {selectedReq && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Solicitante</p>
                    <p className="font-medium">{selectedReq.solicitante_nombre}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Fecha</p>
                    <p className="font-medium">{formatDate(selectedReq.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Departamento</p>
                    <p className="font-medium">{selectedReq.departamento_nombre || "-"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Fecha Requerida</p>
                    <p className="font-medium">{formatDate(selectedReq.fecha_requerida) || "-"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Prioridad</p>
                    <Badge className={PRIORIDAD_CONFIG[selectedReq.prioridad]?.color}>
                      {PRIORIDAD_CONFIG[selectedReq.prioridad]?.label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-slate-500">Estado</p>
                    <Badge className={ESTADO_CONFIG[selectedReq.estado]?.color}>
                      {ESTADO_CONFIG[selectedReq.estado]?.label}
                    </Badge>
                  </div>
                </div>

                {selectedReq.motivo && (
                  <div>
                    <p className="text-slate-500 text-sm">Motivo</p>
                    <p className="text-sm bg-slate-50 p-2 rounded">{selectedReq.motivo}</p>
                  </div>
                )}

                <div>
                  <p className="text-slate-500 text-sm mb-2">Artículos Solicitados</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Artículo</TableHead>
                        <TableHead className="text-right">Solicitado</TableHead>
                        <TableHead className="text-right">Aprobado</TableHead>
                        <TableHead className="text-right">Entregado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedReq.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-sm">{item.articulo?.codigo}</TableCell>
                          <TableCell>{item.articulo?.nombre}</TableCell>
                          <TableCell className="text-right">{item.cantidad_solicitada || item.cantidad} {item.articulo?.unidad_medida || item.unidad_medida}</TableCell>
                          <TableCell className="text-right text-blue-600">{item.cantidad_aprobada || 0}</TableCell>
                          <TableCell className="text-right text-green-600">{item.cantidad_entregada || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {selectedReq.aprobador_nombre && (
                  <div className="text-sm text-slate-500">
                    <p>Aprobado por: {selectedReq.aprobador_nombre} el {formatDate(selectedReq.fecha_aprobacion)}</p>
                    {selectedReq.observaciones_aprobacion && (
                      <p className="mt-1">Observaciones: {selectedReq.observaciones_aprobacion}</p>
                    )}
                  </div>
                )}

                {Array.isArray(selectedReq.historial) && selectedReq.historial.length > 0 && (
                  <div className="mt-4 border-t pt-4" data-testid="requisicion-historial">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Historial de acciones</h4>
                    <div className="space-y-2">
                      {selectedReq.historial.map((h, idx) => {
                        const labels = { aprobada: "Aprobada", rechazada: "Rechazada", entrega_parcial: "Entrega parcial", entrega_total: "Entrega total" };
                        const colors = { aprobada: "bg-blue-500", rechazada: "bg-red-500", entrega_parcial: "bg-amber-500", entrega_total: "bg-green-500" };
                        return (
                          <div key={h.id || idx} className="flex items-start gap-3 text-sm" data-testid={`historial-item-${idx}`}>
                            <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${colors[h.accion] || "bg-slate-400"}`} />
                            <div className="min-w-0">
                              <p className="text-slate-700">
                                <span className="font-medium">{labels[h.accion] || h.accion}</span>
                                {" por "}<span className="font-medium">{h.usuario_nombre}</span>
                                <span className="text-slate-400"> · {formatDate(h.fecha)}</span>
                              </p>
                              {h.detalle && <p className="text-slate-500 break-words">{h.detalle}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Authorization Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <ShieldAlert className="w-5 h-5" />
                Autorización Requerida
              </DialogTitle>
              <DialogDescription>
                Para eliminar la requisición <strong>{reqToDelete?.numero}</strong>, ingrese su contraseña para confirmar.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Acción irreversible
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Solo usuarios con el permiso "Eliminar requisiciones" o administradores pueden autorizar esta acción.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="auth-password">Contraseña</Label>
                <Input
                  id="auth-password"
                  type="password"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  placeholder="••••••••"
                  onKeyDown={(e) => e.key === "Enter" && executeDelete()}
                  data-testid="auth-password-input"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteDialog(false)}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={executeDelete}
                disabled={deleting || !authPassword}
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Autorizar y Eliminar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
